import { useMemo } from "react";
import {
  Order,
  OrderItem,
  OrderItemStatus,
  MenuComboOrderItem,
  MenuFormulaOrderItem,
} from "@/redux/services/order/order.type";
import { useChangeOrderStatusMutation } from "@/redux/services/order/order.api";
import { cn, getOrderTypeLabel } from "@/lib/utils";
import KdsItemRow from "./KdsItemRow";
import { useElapsedTime } from "@/hooks/useElapsedTime";
import {
  Bike,
  GripVertical,
  Package,
  StickyNote,
  Utensils,
  XCircle,
} from "lucide-react";
import { useOrderCloseConfirm } from "@/hooks/useOrderCloseConfirm";
import {
  isCollectedMoneyViolation,
  useCollectedMoneyCancelBlock,
} from "@/hooks/useCollectedMoneyCancelBlock";
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core";

interface KdsOrderCardProps {
  order: Order;
  onItemStatusChange?: () => void;
  dragHandleProps?: {
    listeners?: DraggableSyntheticListeners;
    attributes?: DraggableAttributes;
  };
}

type Selection = { name: string; options: string[] };

function summarizeItemStatuses(order: Order): {
  total: number;
  ready: number;
} {
  let total = 0;
  let ready = 0;
  const tally = (s: OrderItemStatus | undefined) => {
    total += 1;
    if (s === "ready") ready += 1;
  };
  order.orderItems.forEach((i) => tally(i.itemStatus ?? "pending"));
  order.menuComboOrderItems?.forEach((i) =>
    tally((i.itemStatus ?? "pending") as OrderItemStatus),
  );
  order.menuFormulaOrderItems?.forEach((i) =>
    tally((i.itemStatus ?? "pending") as OrderItemStatus),
  );
  return { total, ready };
}

function getElapsedMinutes(createdAt: string): number {
  return (Date.now() - new Date(createdAt).getTime()) / 60000;
}

function ageProgressColor(minutes: number): string {
  if (minutes < 10) return "bg-success";
  if (minutes < 20) return "bg-warning";
  return "bg-destructive";
}

function ageProgressWidth(minutes: number): string {
  const pct = Math.min(100, Math.max(4, (minutes / 30) * 100));
  return `${pct}%`;
}

type OptionGroup = {
  title: string;
  shouldDisplayTitle: boolean;
  values: string[];
};

function formatOptionGroups(groups: OptionGroup[]): string[] {
  return groups.map((g) =>
    g.shouldDisplayTitle
      ? `${g.title}: ${g.values.join(", ")}`
      : g.values.join(", "),
  );
}

function describeOptions(item: OrderItem): string[] {
  if (!item.selectedMenuItemOptionValues?.length) return [];
  const grouped = new Map<string, OptionGroup>();
  for (const value of item.selectedMenuItemOptionValues) {
    const opt = value.menuItemOption;
    const key = opt?.id ?? opt?.title ?? "Option";
    const existing = grouped.get(key);
    const group: OptionGroup = existing ?? {
      title: opt?.title ?? "Option",
      shouldDisplayTitle: opt?.shouldDisplayTitle ?? true,
      values: [],
    };
    group.values.push(value.title);
    grouped.set(key, group);
  }
  return formatOptionGroups(Array.from(grouped.values()));
}

function describeSelectedOptions(selectedOptions: any[] | undefined): string[] {
  if (!selectedOptions?.length) return [];
  const grouped = new Map<string, OptionGroup>();
  for (const opt of selectedOptions) {
    const option = opt.menuItemOption;
    const key = option?.id ?? option?.title ?? "Option";
    const existing = grouped.get(key);
    const group: OptionGroup = existing ?? {
      title: option?.title ?? "Option",
      shouldDisplayTitle: option?.shouldDisplayTitle ?? true,
      values: [],
    };
    group.values.push(opt.title ?? "");
    grouped.set(key, group);
  }
  return formatOptionGroups(Array.from(grouped.values()));
}

function describeComboSelections(item: MenuComboOrderItem): Selection[] {
  return (item.selections ?? [])
    .map((s) => {
      const name = s.menuItem?.title;
      if (!name) return null;
      return {
        name,
        options: describeSelectedOptions(s.selectedOptions),
      };
    })
    .filter((x): x is Selection => x !== null);
}

function describeFormulaSelections(item: MenuFormulaOrderItem): Selection[] {
  return (item.selections ?? [])
    .map((s) => {
      const name =
        s.menuFormulaComponentItem?.name ??
        s.menuItem?.title ??
        s.menuFormulaComponentItem?.menuItem?.title;
      if (!name) return null;
      return {
        name,
        options: describeSelectedOptions(s.selectedOptions),
      };
    })
    .filter((x): x is Selection => x !== null);
}

const KDS_REFUND_HINT = "Demandez à la caisse de rembourser le client.";

const KdsOrderCard: React.FC<KdsOrderCardProps> = ({
  order,
  onItemStatusChange,
  dragHandleProps,
}) => {
  const { text: elapsed, colorClass: elapsedColor } = useElapsedTime(
    order.createdAt,
    order.status,
  );
  const [changeOrderStatus, { isLoading: isUpdatingStatus }] =
    useChangeOrderStatusMutation();
  const confirmStatusChange = useOrderCloseConfirm();
  const { blockIfCollected, showBlocked } = useCollectedMoneyCancelBlock();

  const { total, ready } = useMemo(
    () => summarizeItemStatuses(order),
    [order],
  );

  const allItemsReady = total > 0 && ready === total;
  const ageMinutes = getElapsedMinutes(order.createdAt);
  const isUrgent = ageMinutes >= 20 && order.status !== "ready";

  const sortedRows = useMemo(() => {
    type Row = {
      key: string;
      itemStatus: OrderItemStatus;
      props: React.ComponentProps<typeof KdsItemRow>;
    };
    const rows: Row[] = [];

    for (const item of order.orderItems) {
      const status = (item.itemStatus ?? "pending") as OrderItemStatus;
      rows.push({
        key: item.id,
        itemStatus: status,
        props: {
          itemId: item.id,
          name: item.dailyDish
            ? item.dailyDish.name
            : item.menuItem?.title ?? "Article",
          quantity: item.quantity,
          itemStatus: status,
          orderId: order.id,
          options: describeOptions(item),
          note: item.additionalDemands || null,
          onStatusChange: onItemStatusChange,
        },
      });
    }

    for (const item of order.menuComboOrderItems ?? []) {
      const status = (item.itemStatus ?? "pending") as OrderItemStatus;
      rows.push({
        key: item.id,
        itemStatus: status,
        props: {
          itemId: item.id,
          name: item.menuCombo?.name ?? "Combo",
          quantity: item.quantity,
          itemStatus: status,
          orderId: order.id,
          isCombo: true,
          selections: describeComboSelections(item),
          note: item.additionalDemands || null,
          onStatusChange: onItemStatusChange,
        },
      });
    }

    for (const item of order.menuFormulaOrderItems ?? []) {
      const status = (item.itemStatus ?? "pending") as OrderItemStatus;
      rows.push({
        key: item.id,
        itemStatus: status,
        props: {
          itemId: item.id,
          name: item.menuFormula?.name ?? "Formule",
          quantity: item.quantity,
          itemStatus: status,
          orderId: order.id,
          isFormula: true,
          selections: describeFormulaSelections(item),
          note: item.additionalDemands || null,
          onStatusChange: onItemStatusChange,
        },
      });
    }

    const STATUS_PRIORITY: Record<OrderItemStatus, number> = {
      preparing: 0,
      pending: 1,
      ready: 2,
    };
    return rows.sort(
      (a, b) => STATUS_PRIORITY[a.itemStatus] - STATUS_PRIORITY[b.itemStatus],
    );
  }, [order, onItemStatusChange]);

  const canCancel =
    order.status === "accepted" || order.status === "preparing";

  const orderNote =
    typeof order.notes === "string" ? order.notes.trim() : "";

  const primaryLabel =
    order.dailySequence != null ? `${order.dailySequence}` : order.orderNumber;
  const tableCode = order.restaurantTable?.code
    ? order.restaurantTable.code
    : null;
  const typeIcon =
    order.orderType === "takeaway" ? (
      <Package className="h-5 w-5" />
    ) : order.orderType === "delivery" ? (
      <Bike className="h-5 w-5" />
    ) : (
      <Utensils className="h-5 w-5" />
    );
  const typeText =
    order.orderType === "dine_in" && tableCode
      ? `Sur place · ${tableCode}`
      : order.orderType
        ? getOrderTypeLabel(order.orderType)
        : tableCode
          ? `Sur place · ${tableCode}`
          : null;
  const typeBadgeColor =
    order.orderType === "takeaway"
      ? "bg-warning/15 text-warning border-warning/30"
      : order.orderType === "delivery"
        ? "bg-blue-100 text-blue-700 border-blue-200"
        : "bg-primary-light text-primary border-primary/20";

  const handleCancel = async () => {
    if (!canCancel || isUpdatingStatus) return;
    // An order holding collected money can't be canceled until it is
    // refunded, which the kitchen doesn't do (SFAPI-114).
    if (await blockIfCollected(order, "canceled", { refundHint: KDS_REFUND_HINT })) {
      return;
    }
    const confirmation = await confirmStatusChange(order, "canceled");
    if (!confirmation.proceed) return;
    try {
      await changeOrderStatus({ id: order.id, status: "canceled" }).unwrap();
    } catch (error) {
      if (isCollectedMoneyViolation(error)) {
        await showBlocked("canceled", null, { refundHint: KDS_REFUND_HINT });
      }
      // Other errors are not surfaced here (unchanged behaviour).
    }
  };

  return (
    <article
      className={cn(
        "bg-card rounded-xl border-2 overflow-hidden",
        allItemsReady
          ? "border-success"
          : isUrgent
            ? "border-destructive"
            : "border-border",
      )}
    >
      {/* Age stripe — silent visual cue */}
      <div className="h-1.5 w-full bg-muted">
        <div
          className={cn("h-full transition-all duration-500", ageProgressColor(ageMinutes))}
          style={{ width: ageProgressWidth(ageMinutes) }}
          aria-hidden
        />
      </div>

      {/* Compact header — drag handle when draggable (long-press to drag) */}
      <header
        {...(dragHandleProps?.attributes ?? {})}
        {...(dragHandleProps?.listeners ?? {})}
        className={cn(
          "flex items-start justify-between gap-3 px-4 pt-3 pb-2 border-b border-border",
          dragHandleProps &&
            "cursor-grab active:cursor-grabbing select-none touch-manipulation",
        )}
      >
        <div className="min-w-0 flex items-start gap-2">
          {dragHandleProps && (
            <GripVertical
              className="h-5 w-5 text-muted-foreground/60 mt-1 flex-shrink-0"
              aria-hidden
            />
          )}
          <div className="min-w-0 flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-3xl font-extrabold tabular-nums text-card-foreground leading-none">
                #{primaryLabel}
              </span>
              {typeText && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 text-sm font-bold px-2.5 py-1 rounded-md border",
                    typeBadgeColor,
                  )}
                >
                  {typeIcon}
                  {typeText}
                </span>
              )}
            </div>
            {order.dailySequence != null && (
              <span className="text-sm font-mono text-muted-foreground truncate">
                {order.orderNumber}
              </span>
            )}
          </div>
        </div>
        <span className={cn("text-2xl font-extrabold tabular-nums leading-none pt-1", elapsedColor)}>
          {elapsed}
        </span>
      </header>

      {/* Order-level note — surfaces customer/staff instructions to the kitchen */}
      {orderNote && (
        <div className="px-3 pt-2">
          <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2">
            <StickyNote className="h-4 w-4 text-yellow-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-semibold text-yellow-900 leading-snug whitespace-pre-wrap">
              {orderNote}
            </p>
          </div>
        </div>
      )}

      {/* Items — sorted: preparing → pending → ready */}
      <div className="p-2 space-y-1">
        {sortedRows.map((row) => (
          <KdsItemRow key={row.key} {...row.props} />
        ))}
      </div>

      {/* Cancel — subtle footer action */}
      {canCancel && (
        <div className="border-t border-border">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isUpdatingStatus}
            className={cn(
              "w-full flex items-center justify-center gap-1.5 px-3 py-2.5",
              "text-xs font-medium text-muted-foreground",
              "hover:text-destructive hover:bg-destructive/5",
              "disabled:opacity-50 transition-colors",
            )}
          >
            <XCircle className="h-3.5 w-3.5" />
            Annuler la commande
          </button>
        </div>
      )}

    </article>
  );
};

export default KdsOrderCard;
